/**
 * @file Copied from @mui/material/StepIcon/StepIcon v5.8.4.
 *
 * Edits:
 * - always render StepIconRoot, now a div (instead of an SvgIcon) - even if rendering a custom icon;
 * - StepIconRoot now controls the coloring;
 * - always render both a CheckMark and the 'icon', only manipulate opacity based on state;
 * - use @o2/icons/CheckMark instead of @mui/icons-material/CheckCircle for the _completed_ state;
 * - restyle non-active, non-completed and non-error - remove background, add border;
 * - increase transition duration and add newly used CSS properties to it;
 * - remove 'error' support;
 */

import clsx from 'clsx';
import { ElementType, forwardRef } from 'react';

// TODO: using a transitive dep?
import { isValidElementType } from 'react-is';

import { unstable_composeClasses as composeClasses } from '@mui/base';
import { StepIconProps as MuiStepIconProps } from '@mui/material/StepIcon';

import { Duration, styled, useThemeProps } from '@mui/material/styles';

import SvgIcon from '../SvgIcon';
import CurrentCircle from './CurrentCircle';
import InvertedCheckMarkFillIcon from './InvertedCheckMarkFill';
import stepIconClasses, { getStepIconUtilityClass } from './stepIconClasses';
import pxToRem from '../theme/pxToRem';

export * from '@mui/material/StepIcon';

export interface StepIconProps extends Omit<MuiStepIconProps, 'error'> {}

type StepIconOwnerState = Partial<StepIconProps>;
interface SubcomponentProps {
  ownerState: StepIconOwnerState;
}

const useUtilityClasses = (ownerState: StepIconOwnerState) => {
  const { classes, active, completed } = ownerState;

  const slots = {
    root: ['root', active && 'active', completed && 'completed'],
    text: ['text'],
    icon: ['icon'],
    checkmark: ['checkmark'],
    dot: ['dot'],
  };

  return composeClasses(slots, getStepIconUtilityClass, classes);
};

const animationDuration: keyof Duration = 'standard';

const StepIconRoot = styled('div', {
  name: 'MuiStepIcon',
  slot: 'Root',
  overridesResolver: (props, styles) => styles.root,
})<SubcomponentProps>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  width: pxToRem(24),
  height: pxToRem(24),
  position: 'relative',

  border: `1px solid ${theme.palette.text.disabled}`,
  borderRadius: '50%',
  color: theme.palette.text.disabled,
  [`&.${stepIconClasses.completed}, &.${stepIconClasses.active}`]: {
    background: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [theme.breakpoints.down('md')]: {
    [`&.${stepIconClasses.completed}`]: {
      cursor: 'pointer',
    },
  },
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: theme.transitions.duration[animationDuration],
  }),
}));

const StepIconText = styled('span', {
  name: 'MuiStepIcon',
  slot: 'Text',
  overridesResolver: (props, styles) => styles.text,
})<SubcomponentProps>(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: pxToRem(theme.typography.fontSize),
  fontWeight: theme.typography.fontWeightMedium,
  position: 'absolute',
}));

const StepIconIcon = styled(SvgIcon)<SubcomponentProps>(({ theme }) => ({
  position: 'absolute',

  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration[animationDuration],
  }),
}));

const StepIconCompletedIcon = styled(StepIconIcon)(({ ownerState: { completed } }) => ({
  opacity: completed ? 1 : 0,
}));

const StepIconCurrentIcon = styled(StepIconIcon)(({ ownerState: { active } }) => ({
  opacity: active ? 1 : 0,
}));

const StepIcon = forwardRef<HTMLDivElement, StepIconProps>((inProps, ref) => {
  const props = useThemeProps({ props: inProps, name: 'MuiStepIcon' });
  const {
    active = false,
    className: classNameProp,
    completed = false,
    icon,
    //@ts-expect-error: 'error' is added from the MUI context but not supported by @o2/ui (confirmed by Julo, 2022-08-30)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error: _error,
    ...other
  } = props;

  const ownerState = { ...props, active, completed };
  const classes = useUtilityClasses(ownerState);

  const className = clsx(classNameProp, classes.root);

  return (
    <StepIconRoot className={className} ownerState={ownerState} {...other} ref={ref}>
      <StepIconCompletedIcon className={classes.checkmark} as={InvertedCheckMarkFillIcon} ownerState={ownerState} />
      <StepIconCurrentIcon className={classes.dot} ownerState={ownerState}>
        <CurrentCircle />
      </StepIconCurrentIcon>
      {icon && isValidElementType(icon) ? (
        <StepIconIcon
          className={classes.icon}
          as={icon as ElementType}
          ownerState={ownerState}
          sx={{ opacity: completed ? 0 : 1 }}
        />
      ) : (
        <StepIconText className={classes.text} ownerState={ownerState}>
          {icon}
        </StepIconText>
      )}
    </StepIconRoot>
  );
});

StepIcon.displayName = 'StepIcon';

export default StepIcon;
